import React, { useState, useEffect } from 'react';
import UISectionWrapper, { UISectionHeader, UISectionBody } from '@/components/shared/UISectionWrapper';
import UISelect from '@/components/shared/UISelect';
import UICircleChartWithLegend from '@/components/shared/UICircleChartWithLegend';
import { UICircleChartSegment } from '@/components/shared/UICircleChart';
import { usePayments } from '@/hooks/usePayments';
import { PeriodStatisticsData } from '@/api';
import './PaymentsOverviewSection.scss';

interface PaymentsOverviewData {
  paymentsCount: number;
  arrearsCount: number;
  warningsCount: number;
  paidPercentage: number;
  arrearsPercentage: number;
  warningsPercentage: number;
  receivedPayments: number;
  plannedIncome: number;
  arrears: number;
}

interface PaymentsOverviewSectionProps {
  data?: PaymentsOverviewData;
  selectedPeriod?: string;
  onPeriodChange?: (period: string) => void;
  onToggleCollapse?: (isCollapsed: boolean) => void;
}

const periodOptions = [
  { value: 'this-month', label: 'This month' },
  { value: 'last-month', label: 'Last month' },
  { value: 'this-quarter', label: 'This quarter' },
  { value: 'this-year', label: 'This year' },
];

const PaymentsOverviewSection: React.FC<PaymentsOverviewSectionProps> = ({
                                                                           data: externalData,
                                                                           selectedPeriod: externalSelectedPeriod,
                                                                           onPeriodChange = () => {},
                                                                           onToggleCollapse = () => {},
                                                                         }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [internalSelectedPeriod, setInternalSelectedPeriod] = useState<string>('this-month');
  const [statisticsData, setStatisticsData] = useState<PeriodStatisticsData | null>(null);
  const [isDataLoading, setIsDataLoading] = useState(false);

  const { getPaymentStatisticsByPeriod, loading, error } = usePayments();

  // Використовуємо зовнішній selectedPeriod якщо переданий, інакше внутрішній
  const selectedPeriod = externalSelectedPeriod || internalSelectedPeriod;

  // Завантажуємо дані при монтуванні та зміні періоду
  useEffect(() => {
    loadStatistics(selectedPeriod as 'this-month' | 'last-month' | 'this-quarter' | 'this-year');
  }, [selectedPeriod]);

  const loadStatistics = async (period: 'this-month' | 'last-month' | 'this-quarter' | 'this-year') => {
    setIsDataLoading(true);
    try {
      const data = await getPaymentStatisticsByPeriod(period);
      if (data) {
        setStatisticsData(data);
      }
    } catch (err) {
      console.error('Failed to load payment statistics:', err);
    } finally {
      setIsDataLoading(false);
    }
  };

  const handleToggleCollapse = () => {
    const newCollapsed = !isCollapsed;
    setIsCollapsed(newCollapsed);
    onToggleCollapse(newCollapsed);
  };

  const handlePeriodChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newPeriod = event.target.value;

    // Оновлюємо внутрішній стан якщо не використовується зовнішній
    if (!externalSelectedPeriod) {
      setInternalSelectedPeriod(newPeriod);
    }

    // Викликаємо зовнішній обробник
    onPeriodChange(newPeriod);
  };

  // Використовуємо реальні дані якщо є, інакше зовнішні дані, інакше fallback
  const getCurrentData = (): PaymentsOverviewData => {
    if (statisticsData) {
      // Мапимо дані з API до формату компонента
      const total = statisticsData.total || 1; // Запобігаємо діленню на нуль

      return {
        paymentsCount: statisticsData.paid,
        arrearsCount: statisticsData.arrears,
        warningsCount: statisticsData.upcoming, // Замінюємо warnings на upcoming
        paidPercentage: total > 0 ? Math.round((statisticsData.paid / total) * 100) : 0,
        arrearsPercentage: total > 0 ? Math.round((statisticsData.arrears / total) * 100) : 0,
        warningsPercentage: total > 0 ? Math.round((statisticsData.upcoming / total) * 100) : 0,
        receivedPayments: statisticsData.amounts.totalPaid,
        plannedIncome: statisticsData.amounts.totalPaid + statisticsData.amounts.totalUpcoming + statisticsData.amounts.totalOverdue,
        arrears: statisticsData.amounts.totalOverdue,
      };
    }

    if (externalData) {
      return externalData;
    }

    // Fallback дані якщо нічого немає
    return {
      paymentsCount: 0,
      arrearsCount: 0,
      warningsCount: 0,
      paidPercentage: 0,
      arrearsPercentage: 0,
      warningsPercentage: 0,
      receivedPayments: 0,
      plannedIncome: 0,
      arrears: 0,
    };
  };

  const currentData = getCurrentData();

  // Підготовка даних для графіка
  const chartSegments: UICircleChartSegment[] = [
    {
      value: currentData.paymentsCount,
      percentage: currentData.paidPercentage,
      color: '#76C102',
      label: 'Paid',
    },
    {
      value: currentData.arrearsCount,
      percentage: currentData.arrearsPercentage,
      color: '#FB2F54',
      label: 'Arrears',
    },
    {
      value: currentData.warningsCount,
      percentage: currentData.warningsPercentage,
      color: '#FF9C07',
      label: 'Upcoming', // Змінено з "Warnings" на "Upcoming"
    },
  ];

  return (
    <UISectionWrapper spacing="default" align="stretch" shadow="default">
      <UISectionHeader>
        <div className="PaymentsOverviewSection__header">
          <div className="PaymentsOverviewSection__title-row">
            <div className="PaymentsOverviewSection__title-content">
              <h2 className="PaymentsOverviewSection__title">Overview for</h2>
              <UISelect
                size="medium"
                value={selectedPeriod}
                onChange={handlePeriodChange}
                options={periodOptions}
                className="PaymentsOverviewSection__period-select"
                disabled={loading || isDataLoading}
              />
            </div>
            <button
              className="PaymentsOverviewSection__collapse-btn"
              onClick={handleToggleCollapse}
              aria-label={isCollapsed ? 'Expand section' : 'Collapse section'}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                className={`PaymentsOverviewSection__collapse-icon ${isCollapsed ? 'PaymentsOverviewSection__collapse-icon--collapsed' : ''}`}
              >
                <path
                  d="M12 6L8 10L4 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
          <div className="PaymentsOverviewSection__separator"></div>
        </div>
      </UISectionHeader>

      {!isCollapsed && (
        <UISectionBody>
          <div className="PaymentsOverviewSection__content">
            {/* Показуємо лоадер або помилку */}
            {(loading || isDataLoading) && (
              <div className="PaymentsOverviewSection__loading">
                Loading statistics...
              </div>
            )}

            {error && (
              <div className="PaymentsOverviewSection__error">
                Error loading statistics: {error}
              </div>
            )}

            {/* Основний контент */}
            {!loading && !isDataLoading && (
              <div className="PaymentsOverviewSection__stats">
                {/* Left section - Stats cards + Chart + Legend */}
                <div className="PaymentsOverviewSection__stats-left">
                  {/* Stats Cards */}
                  <div className="PaymentsOverviewSection__stats-cards">
                    <div className="PaymentsOverviewSection__stat-card PaymentsOverviewSection__stat-card--success">
                      <span className="PaymentsOverviewSection__stat-label">Payments</span>
                      <span className="PaymentsOverviewSection__stat-value">{currentData.paymentsCount}</span>
                    </div>
                    <div className="PaymentsOverviewSection__stat-card PaymentsOverviewSection__stat-card--danger">
                      <span className="PaymentsOverviewSection__stat-label">Arrears</span>
                      <span className="PaymentsOverviewSection__stat-value">{currentData.arrearsCount}</span>
                    </div>
                    <div className="PaymentsOverviewSection__stat-card PaymentsOverviewSection__stat-card--warning">
                      <span className="PaymentsOverviewSection__stat-label">Upcoming</span>
                      <span className="PaymentsOverviewSection__stat-value">{currentData.warningsCount}</span>
                    </div>
                  </div>

                  {/* Chart + Legend */}
                  <div className="PaymentsOverviewSection__chart-section">
                    <UICircleChartWithLegend
                      segments={chartSegments}
                      size={104}
                      strokeWidth={16}
                      legendPosition="left"
                      gap={32}
                    />
                  </div>
                </div>

                {/* Right section - Info blocks */}
                <div className="PaymentsOverviewSection__stats-right">
                  <div className="PaymentsOverviewSection__info-block">
                    <div className="PaymentsOverviewSection__info-title">
                      <div className="PaymentsOverviewSection__info-icon PaymentsOverviewSection__info-icon--success">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <span className="PaymentsOverviewSection__info-label">Received Payments</span>
                    </div>
                    <span className="PaymentsOverviewSection__info-value">$ {currentData.receivedPayments.toLocaleString()}</span>
                  </div>

                  <div className="PaymentsOverviewSection__info-block">
                    <div className="PaymentsOverviewSection__info-title">
                      <div className="PaymentsOverviewSection__info-icon PaymentsOverviewSection__info-icon--info">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5M9 5C9 6.10457 9.89543 7 11 7H13C14.1046 7 15 6.10457 15 7M9 12H15M9 16H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <span className="PaymentsOverviewSection__info-label">Planned Income</span>
                    </div>
                    <span className="PaymentsOverviewSection__info-value">$ {currentData.plannedIncome.toLocaleString()}</span>
                  </div>

                  <div className="PaymentsOverviewSection__info-block">
                    <div className="PaymentsOverviewSection__info-title">
                      <div className="PaymentsOverviewSection__info-icon PaymentsOverviewSection__info-icon--danger">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <span className="PaymentsOverviewSection__info-label">Arrears</span>
                    </div>
                    <span className="PaymentsOverviewSection__info-value">$ {currentData.arrears.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </UISectionBody>
      )}
    </UISectionWrapper>
  );
};

export default PaymentsOverviewSection;